import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IoMdEye } from 'react-icons/io';
import { MdDelete } from "react-icons/md";
import { Pagination } from '@mui/material';
import ViewWarehouse from '../../../../component/b2c/models/ViewWarehouse';
import { useMyContext } from '../../../../context/MyContext';
import Swal from 'sweetalert2';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import PaginationItem from '@mui/material/PaginationItem';
import '../../../../css/custom-scrollbar.css';
import {
  LuWarehouse,
  LuPackageSearch
} from "react-icons/lu";
import axios from 'axios';
import { IoClose } from "react-icons/io5";
import { Base_Url, deletewarehouse, Warehouselist } from '../../../../config/config';
import AddWarehousePopup from '../addwarehouse/AddWarehousePopup.jsx';
import {
  Plus,
  Search,
  RefreshCw,
  LayoutGrid,
  LayoutList,
  Mail,
  User,
  Copy,
  Check,
  MapPin,
  Phone
} from 'lucide-react';

const AllWarehouses = () => {
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode, isToggleSidebar, setIsToggleSidebar } = useMyContext();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [loadingWarehouseId, setloadingWarehouseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Controls the right-side "Add Warehouse" drawer
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  // Mobile detection
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobileView(window.innerWidth < 768);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  const fetchWarehouses = useCallback(async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    setIsRefreshing(true);
    try {
      const pageNumber = Number(page) || 1;
      const limitNumber = Number(limit) || 10;
      const urlWarehouse = `${Base_Url}${Warehouselist}?page=${pageNumber}&limit=${limitNumber}`;
      const response = await fetch(urlWarehouse, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === "SUCCESS" && Array.isArray(result.data)) {
        setWarehouses(result.data);
        setTotalOrders(result.pagination?.totalItems || result.data.length || 0);
      } else {
        console.error('Unexpected response format:', result);
      }
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleViewClick = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsPopupOpen(true);
  };

  const handleClose = () => {
    setIsPopupOpen(false);
  };

  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  const handleDelete = async (id) => {
    setloadingWarehouseId(id);
    Swal.fire({
      title: 'Delete Warehouse?',
      text: "You won't be able to revert this deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        popup: isDarkMode ? '!bg-slate-900 !text-white !border !border-slate-800' : ''
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const urldelete = Base_Url + deletewarehouse;
        try {
          const token = localStorage.getItem('token');
          const cusid = localStorage.getItem('cus_id');
          const data = { warehouseId: id };
          const response = await axios.post(urldelete, data, {
            headers: {
              token,
              cus_id: cusid,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
          });

          if (response.data.status === "SUCCESS") {
            setWarehouses((prevWarehouses) => prevWarehouses.filter(warehouse => warehouse.id !== id));
            setTotalOrders((prev) => Math.max(0, prev - 1));

            Swal.fire({
              title: 'Deleted!',
              text: response.data.message || 'Warehouse has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#4f46e5',
              customClass: {
                popup: isDarkMode ? '!bg-slate-900 !text-white !border !border-slate-800' : ''
              }
            });
          } else {
            Swal.fire('Error!', response.data.message || 'Failed to delete warehouse.', 'error');
          }
        } catch (error) {
          Swal.fire('Error!', error.message || 'Error deleting warehouse.', 'error');
        } finally {
          setloadingWarehouseId(null);
        }
      } else {
        setloadingWarehouseId(null);
      }
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtered warehouses by search term
  const displayWarehouses = useMemo(() => {
    if (!searchTerm.trim()) return warehouses;
    const term = searchTerm.toLowerCase();
    return warehouses.filter((warehouse) => (
      warehouse.name?.toString().toLowerCase().includes(term) ||
      warehouse.phone?.toString().toLowerCase().includes(term) ||
      warehouse.email?.toString().toLowerCase().includes(term) ||
      warehouse.city?.toString().toLowerCase().includes(term) ||
      warehouse.state?.toString().toLowerCase().includes(term) ||
      warehouse.contact_person?.toString().toLowerCase().includes(term) ||
      warehouse.id?.toString().toLowerCase().includes(term)
    ));
  }, [warehouses, searchTerm]);

  const handleAddSuccess = () => {
    setIsAddDrawerOpen(false);
    setPage(1);
    fetchWarehouses();
  };

  // Deterministic gradient per warehouse avatar
  const avatarGradients = [
    'from-indigo-500 to-purple-600',
    'from-purple-500 to-pink-600',
    'from-rose-500 to-amber-600',
    'from-emerald-500 to-teal-600',
    'from-blue-500 to-cyan-600',
    'from-violet-500 to-indigo-600',
    'from-fuchsia-500 to-rose-600',
    'from-teal-500 to-emerald-600',
  ];
  const getAvatarGradient = (seed) => {
    const str = (seed || 'W').toString();
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return avatarGradients[Math.abs(hash) % avatarGradients.length];
  };

  const isAllMode = totalOrders > 0 && limit >= totalOrders;

  return (
    <>
      <div className={`relative w-full max-w-full h-screen overflow-hidden flex flex-col transition-colors duration-300 ${isDarkMode ? 'dark bg-[#0B1120]' : 'bg-gray-50'}`}>
        {/* Ambient background glows strictly contained */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
          {isDarkMode ? (
            <>
              <div className="absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
              <div className="absolute top-1/2 -right-40 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
            </>
          ) : (
            <>
              <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />
              <div className="absolute top-2/3 -right-24 h-80 w-80 rounded-full bg-purple-200/30 blur-3xl" />
            </>
          )}
        </div>

        <NavBar />

        <div className="flex flex-1 min-h-0 overflow-hidden relative z-10">
          {/* Mobile sidebar overlay */}
          {isToggleSidebar && (
            <div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
              onClick={() => setIsToggleSidebar(false)}
            />
          )}

          {/* Sidebar container */}
          <div
            className={`
              fixed inset-y-0 left-0 z-50 w-64
              ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
              md:static md:inset-auto md:z-auto md:translate-x-0
              ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
              m-2 sm:m-3 rounded-3xl overflow-hidden transition-all duration-300 ease-in-out
            `}
          >
            <Sidebar />
          </div>

          {/* Main content body */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-2.5 sm:p-3 md:p-4">
            <div className={`flex flex-col flex-1 min-h-0 overflow-hidden rounded-2xl border p-3 sm:p-4 md:p-5 transition-colors duration-300 shadow-sm ${isDarkMode
                ? 'bg-gradient-to-br from-[#111827] to-[#0F172A] border-slate-800 shadow-[0_0_30px_rgba(0,0,0,0.4)]'
                : 'bg-white border-slate-200/80 shadow-slate-100'
              }`}>

              {/* 1. TOP TOOLBAR: HEADER + SEARCH + CONTROLS */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3.5 shrink-0">
                {/* Left: Icon, Title & Hub Count */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 via-purple-600 to-violet-500 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/25 ring-2 ring-white/20">
                    <LuWarehouse size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white">
                        All Warehouses
                      </h1>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                        {totalOrders} Hubs
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Manage fulfillment centers, dispatch hubs & pickup locations
                    </p>
                  </div>
                </div>

                {/* Right: Search Bar + View Toggle + Refresh + Create Warehouse */}
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  {/* Search Input */}
                  <div className="relative flex-1 sm:w-60 md:w-68">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by ID, name, phone, city..."
                      className={`w-full pl-8 pr-7 py-2 text-xs rounded-xl border outline-none transition-all ${isDarkMode
                          ? 'bg-slate-900/80 border-slate-700/80 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20'
                          : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 shadow-2xs'
                        }`}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                      >
                        <IoClose size={14} />
                      </button>
                    )}
                  </div>

                  {/* Table vs Grid Switcher (Desktop only) */}
                  <div className="hidden sm:flex items-center p-0.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                    <button
                      type="button"
                      onClick={() => setViewMode('table')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'table'
                          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-2xs font-bold'
                          : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      title="Table View"
                    >
                      <LayoutList size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid'
                          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-2xs font-bold'
                          : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      title="Grid Cards View"
                    >
                      <LayoutGrid size={15} />
                    </button>
                  </div>

                  {/* Refresh Button */}
                  <button
                    type="button"
                    onClick={fetchWarehouses}
                    disabled={isRefreshing}
                    className={`p-2 rounded-xl border transition-all ${isDarkMode
                        ? 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                        : 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                      }`}
                    title="Refresh List"
                  >
                    <RefreshCw size={14} className={isRefreshing ? 'animate-spin text-indigo-500' : ''} />
                  </button>

                  {/* Create Warehouse CTA */}
                  <button
                    type="button"
                    onClick={() => setIsAddDrawerOpen(true)}
                    className="px-3.5 py-2 rounded-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 active:scale-95 text-white transition-all duration-200 shadow-md shadow-indigo-600/25 hover:shadow-indigo-600/40 flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                  >
                    <Plus size={15} />
                    <span>Create Warehouse</span>
                  </button>
                </div>
              </div>

              {/* 2. MAIN CONTENT (DESKTOP TABLE OR GRID CARDS) */}
              {isMobileView ? (
                /* ================= MOBILE CARDS VIEW ================= */
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-2.5 pb-2">
                  {displayWarehouses.length === 0 ? (
                    <div className={`text-center py-12 rounded-2xl border ${isDarkMode ? 'border-slate-800 bg-slate-900/40 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500'
                      }`}>
                      <LuPackageSearch size={36} className="mx-auto mb-2 opacity-40 text-indigo-500" />
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200">No warehouses found</p>
                      <p className="text-xs text-slate-400 mt-0.5">Try adjusting your search query.</p>
                    </div>
                  ) : (
                    displayWarehouses.map((warehouse) => {
                      const isActive = (warehouse.status || 'Active').toLowerCase() === 'active';
                      return (
                        <div
                          key={warehouse.id}
                          className={`rounded-2xl border p-3.5 transition-all shadow-xs relative overflow-hidden ${isDarkMode
                              ? 'bg-slate-900/90 border-slate-800'
                              : 'bg-white border-slate-200'
                            }`}
                        >
                          {/* Top accent line */}
                          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getAvatarGradient(warehouse.id || warehouse.name)}`} />

                          {/* Top row: Avatar, Name, ID & Status */}
                          <div className="flex items-start justify-between gap-2.5 mb-2.5 mt-0.5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-9 h-9 rounded-xl bg-gradient-to-tr ${getAvatarGradient(warehouse.id || warehouse.name)} text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm`}>
                                {warehouse.name?.toString().charAt(0).toUpperCase() || 'W'}
                              </div>
                              <div className="min-w-0">
                                <h3 className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white truncate">
                                  {warehouse.name}
                                </h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="font-mono text-[10px] text-slate-400 font-bold bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded">
                                    #{warehouse.id}
                                  </span>
                                  {warehouse.contact_person && (
                                    <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate flex items-center gap-1">
                                      <User size={10} className="text-indigo-500 shrink-0" />
                                      {warehouse.contact_person}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${isActive
                                ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                              }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                              {warehouse.status || 'Active'}
                            </span>
                          </div>

                          {/* Contact Details */}
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/50 space-y-1.5 mb-3 text-xs">
                            {warehouse.phone && (
                              <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-1.5 font-mono text-[11px]">
                                  <Phone size={11} className="text-indigo-500" />
                                  {warehouse.phone}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(warehouse.phone, `phone-${warehouse.id}`)}
                                  className="text-slate-400 hover:text-indigo-600 p-1"
                                  title="Copy phone"
                                >
                                  {copiedId === `phone-${warehouse.id}` ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                                </button>
                              </div>
                            )}
                            {warehouse.email && (
                              <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 text-[11px] truncate">
                                <Mail size={11} className="text-purple-500 shrink-0" />
                                <span className="truncate">{warehouse.email}</span>
                              </div>
                            )}
                            <div className="flex items-start gap-1.5 text-slate-600 dark:text-slate-400 text-[11px] pt-0.5 border-t border-slate-200/60 dark:border-slate-700/40">
                              <MapPin size={11} className="text-rose-500 shrink-0 mt-0.5" />
                              <span className="leading-tight">
                                {[warehouse.address, warehouse.city, warehouse.state, warehouse.pincode].filter(Boolean).join(', ') || '—'}
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleViewClick(warehouse)}
                              className="flex-1 py-1.5 px-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors border border-indigo-200/80 dark:border-indigo-800/60"
                            >
                              <IoMdEye size={15} />
                              <span>View Details</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(warehouse.id)}
                              disabled={loadingWarehouseId === warehouse.id}
                              className="p-1.5 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 font-bold text-xs flex items-center justify-center gap-1 transition-colors border border-rose-200/80 dark:border-rose-800/60 disabled:opacity-50"
                              title="Delete Warehouse"
                            >
                              {loadingWarehouseId === warehouse.id ? (
                                <RefreshCw size={13} className="animate-spin" />
                              ) : (
                                <MdDelete size={15} />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                /* ================= DESKTOP GRID CARDS VIEW ================= */
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-2">
                  {displayWarehouses.length === 0 ? (
                    <div className={`text-center py-16 rounded-2xl border ${isDarkMode ? 'border-slate-800 bg-slate-900/40 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500'
                      }`}>
                      <LuPackageSearch size={40} className="mx-auto mb-2 opacity-40 text-indigo-500" />
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200">No warehouses found</p>
                      <p className="text-xs text-slate-400 mt-0.5">Try adjusting your search query.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                      {displayWarehouses.map((warehouse) => {
                        const isActive = (warehouse.status || 'Active').toLowerCase() === 'active';
                        return (
                          <div
                            key={warehouse.id}
                            className={`rounded-2xl border p-4 transition-all duration-200 hover:shadow-md relative overflow-hidden flex flex-col justify-between group ${isDarkMode
                                ? 'bg-slate-900/90 border-slate-800/90 hover:border-indigo-500/40'
                                : 'bg-white border-slate-200 hover:border-indigo-300'
                              }`}
                          >
                            {/* Top decorative accent */}
                            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getAvatarGradient(warehouse.id || warehouse.name)}`} />

                            <div>
                              {/* Header: Avatar, Name, ID, Status */}
                              <div className="flex items-start justify-between gap-2.5 mb-3 mt-1">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${getAvatarGradient(warehouse.id || warehouse.name)} text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
                                    {warehouse.name?.toString().charAt(0).toUpperCase() || 'W'}
                                  </div>
                                  <div className="min-w-0">
                                    <h3 className="font-extrabold text-sm text-slate-900 dark:text-white truncate" title={warehouse.name}>
                                      {warehouse.name}
                                    </h3>
                                    <span className="font-mono text-[10px] text-slate-400 font-bold bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded inline-block mt-0.5">
                                      ID: #{warehouse.id}
                                    </span>
                                  </div>
                                </div>

                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${isActive
                                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                  }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                                  {warehouse.status || 'Active'}
                                </span>
                              </div>

                              {/* Details Strip */}
                              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/50 space-y-1.5 text-xs mb-3">
                                {warehouse.contact_person && (
                                  <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 truncate">
                                    <User size={12} className="text-indigo-500 shrink-0" />
                                    <span className="truncate font-medium">{warehouse.contact_person}</span>
                                  </div>
                                )}
                                {warehouse.phone && (
                                  <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                                    <span className="flex items-center gap-1.5 font-mono text-[11px]">
                                      <Phone size={11} className="text-blue-500 shrink-0" />
                                      {warehouse.phone}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleCopy(warehouse.phone, `grid-phone-${warehouse.id}`)}
                                      className="text-slate-400 hover:text-indigo-600 p-0.5 rounded"
                                      title="Copy phone"
                                    >
                                      {copiedId === `grid-phone-${warehouse.id}` ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                                    </button>
                                  </div>
                                )}
                                {warehouse.email && (
                                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 text-[11px] truncate">
                                    <Mail size={11} className="text-purple-500 shrink-0" />
                                    <span className="truncate">{warehouse.email}</span>
                                  </div>
                                )}
                                <div className="flex items-start gap-1.5 text-slate-600 dark:text-slate-400 text-[11px] pt-1 border-t border-slate-200/60 dark:border-slate-700/40">
                                  <MapPin size={11} className="text-rose-500 shrink-0 mt-0.5" />
                                  <span className="leading-tight line-clamp-2">
                                    {[warehouse.address, warehouse.city, warehouse.state, warehouse.pincode].filter(Boolean).join(', ') || '—'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Card Footer Actions */}
                            <div className="flex items-center gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                              <button
                                type="button"
                                onClick={() => handleViewClick(warehouse)}
                                className="flex-1 py-1.5 px-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors border border-indigo-200/80 dark:border-indigo-800/60"
                              >
                                <IoMdEye size={15} />
                                <span>View Details</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(warehouse.id)}
                                disabled={loadingWarehouseId === warehouse.id}
                                className="p-1.5 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 font-bold text-xs flex items-center justify-center gap-1 transition-colors border border-rose-200/80 dark:border-rose-800/60 disabled:opacity-50"
                                title="Delete Warehouse"
                              >
                                {loadingWarehouseId === warehouse.id ? (
                                  <RefreshCw size={13} className="animate-spin" />
                                ) : (
                                  <MdDelete size={15} />
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* ================= DESKTOP TABLE VIEW ================= */
                <div className="flex-1 min-h-0 overflow-hidden flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 shadow-xs">
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto custom-scrollbar">
                    <table className="w-full min-w-[840px] table-fixed text-xs border-separate border-spacing-0">
                      <thead>
                        <tr className="text-slate-500 dark:text-slate-400 text-[11px] uppercase font-bold tracking-wider">
                          <th className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 py-3 px-3.5 text-left border-b border-slate-200 dark:border-slate-700 w-[24%] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                            Warehouse Hub
                          </th>
                          <th className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 py-3 px-3.5 text-left border-b border-slate-200 dark:border-slate-700 w-[26%] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                            Contact Person & Info
                          </th>
                          <th className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 py-3 px-3.5 text-left border-b border-slate-200 dark:border-slate-700 w-[30%] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                            Location & Address
                          </th>
                          <th className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 py-3 px-3 text-center border-b border-slate-200 dark:border-slate-700 w-[10%] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                            Status
                          </th>
                          <th className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 py-3 px-4 text-right border-b border-slate-200 dark:border-slate-700 w-[10%] shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayWarehouses.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-14 text-center">
                              <div className="flex flex-col items-center justify-center text-center gap-2">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-500 flex items-center justify-center">
                                  <LuPackageSearch size={24} />
                                </div>
                                <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                                  No warehouses found
                                </p>
                                <p className="text-xs text-slate-400 max-w-xs">
                                  Try refining your search keyword.
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          displayWarehouses.map((warehouse) => {
                            const isActive = (warehouse.status || 'Active').toLowerCase() === 'active';
                            return (
                              <tr
                                key={warehouse.id}
                                className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors duration-150 group"
                              >
                                {/* 1. Warehouse Name & Avatar */}
                                <td className="py-3 px-3.5 border-b border-slate-100 dark:border-slate-800/80">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className={`w-8 h-8 rounded-xl bg-gradient-to-tr ${getAvatarGradient(warehouse.id || warehouse.name)} text-white font-extrabold text-xs flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform`}>
                                      {warehouse.name?.toString().charAt(0).toUpperCase() || 'W'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="font-extrabold text-xs sm:text-[13px] text-slate-900 dark:text-white truncate" title={warehouse.name}>
                                        {warehouse.name}
                                      </p>
                                      <span className="font-mono text-[10px] text-slate-400 font-bold bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded inline-block mt-0.5">
                                        ID: #{warehouse.id}
                                      </span>
                                    </div>
                                  </div>
                                </td>

                                {/* 2. Contact Person & Phone */}
                                <td className="py-3 px-3.5 border-b border-slate-100 dark:border-slate-800/80">
                                  <div className="min-w-0">
                                    {warehouse.contact_person ? (
                                      <p className="font-bold text-slate-800 dark:text-slate-200 text-xs flex items-center gap-1.5 truncate" title={warehouse.contact_person}>
                                        <User size={11} className="text-indigo-500 shrink-0" />
                                        <span className="truncate">{warehouse.contact_person}</span>
                                      </p>
                                    ) : (
                                      <span className="text-slate-400 text-xs">—</span>
                                    )}

                                    <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                      {warehouse.phone && (
                                        <span className="font-mono text-[11px] text-slate-600 dark:text-slate-400 flex items-center gap-1 shrink-0">
                                          <Phone size={10} className="text-blue-500 shrink-0" />
                                          <span>{warehouse.phone}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleCopy(warehouse.phone, `table-phone-${warehouse.id}`)}
                                            className="text-slate-400 hover:text-indigo-600 p-0.5"
                                            title="Copy Phone"
                                          >
                                            {copiedId === `table-phone-${warehouse.id}` ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
                                          </button>
                                        </span>
                                      )}
                                      {warehouse.email && (
                                        <span className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 truncate min-w-0" title={warehouse.email}>
                                          <Mail size={10} className="text-purple-500 shrink-0" />
                                          <span className="truncate">{warehouse.email}</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* 3. Location / Address */}
                                <td className="py-3 px-3.5 border-b border-slate-100 dark:border-slate-800/80">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {warehouse.city && (
                                        <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800/40 shrink-0">
                                          {warehouse.city}
                                        </span>
                                      )}
                                      {warehouse.state && (
                                        <span className="text-[11px] font-medium text-slate-600 dark:text-slate-400 truncate">
                                          {warehouse.state}
                                        </span>
                                      )}
                                      {warehouse.pincode && (
                                        <span className="font-mono text-[10px] text-slate-400 shrink-0">
                                          ({warehouse.pincode})
                                        </span>
                                      )}
                                    </div>
                                    {warehouse.address && (
                                      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5 leading-snug" title={warehouse.address}>
                                        {warehouse.address}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                {/* 4. Status Badge */}
                                <td className="py-3 px-3 text-center border-b border-slate-100 dark:border-slate-800/80 whitespace-nowrap">
                                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold border ${isActive
                                      ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                    }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                                    <span>{warehouse.status || 'Active'}</span>
                                  </span>
                                </td>

                                {/* 5. Actions */}
                                <td className="py-3 px-4 text-right border-b border-slate-100 dark:border-slate-800/80 whitespace-nowrap">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => handleViewClick(warehouse)}
                                      aria-label="View warehouse"
                                      className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all shadow-2xs"
                                      title="View Details"
                                    >
                                      <IoMdEye size={15} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(warehouse.id)}
                                      aria-label="Delete warehouse"
                                      disabled={loadingWarehouseId === warehouse.id}
                                      className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/50 text-rose-600 dark:text-rose-400 hover:border-rose-300 dark:hover:border-rose-700 transition-all shadow-2xs disabled:opacity-40"
                                      title="Delete Warehouse"
                                    >
                                      {loadingWarehouseId === warehouse.id ? (
                                        <RefreshCw size={13} className="animate-spin" />
                                      ) : (
                                        <MdDelete size={15} />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 3. FOOTER & PAGINATION */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2.5 pt-2.5 mt-2 border-t border-slate-200/80 dark:border-slate-800 shrink-0 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-500 dark:text-slate-400 font-medium">
                    Showing <strong className="text-slate-900 dark:text-white font-bold">{displayWarehouses.length}</strong> of{' '}
                    <strong className="text-slate-900 dark:text-white font-bold">{totalOrders}</strong> warehouses
                  </span>
                  {isAllMode && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                      All {totalOrders}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2.5">
                  {/* Page Size Selector */}
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <span className="hidden sm:inline text-[11px] font-medium">Rows:</span>
                    <select
                      id="limit"
                      value={limit}
                      onChange={handleLimitChange}
                      className="border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs font-semibold outline-none bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500/20 cursor-pointer shadow-2xs"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={totalOrders > 0 ? totalOrders : 10000}>
                        All ({totalOrders > 0 ? totalOrders : 'All'})
                      </option>
                    </select>
                  </div>

                  {/* Material Pagination (shown when there are multiple pages) */}
                  {!isAllMode && Math.ceil(totalOrders / limit) > 1 && (
                    <Pagination
                      count={Math.max(1, Math.ceil(totalOrders / limit))}
                      page={page}
                      onChange={handlePaginationChange}
                      variant="outlined"
                      shape="rounded"
                      size="small"
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          sx={{
                            borderRadius: '8px',
                            color: isDarkMode ? '#E5E7EB' : '#1e293b',
                            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
                            '&.Mui-selected': {
                              backgroundColor: isDarkMode ? '#4f46e5' : '#4f46e5',
                              color: '#ffffff',
                              borderColor: '#4f46e5',
                              fontWeight: 700,
                              '&:hover': {
                                backgroundColor: '#4338ca',
                              }
                            },
                            '&:hover': {
                              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                            }
                          }}
                        />
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View Warehouse Modal Popup */}
        <ViewWarehouse isopen={isPopupOpen} onClose={handleClose} warehouse={selectedWarehouse} />

        {/* Right-side "Add Warehouse" Drawer */}
        <div
          className={`fixed inset-0 z-[60] transition-opacity duration-300 ${isAddDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity"
            onClick={() => setIsAddDrawerOpen(false)}
          />

          {/* Sliding Panel */}
          <div
            className={`absolute top-0 right-0 h-full w-full sm:w-[540px] md:w-[600px] shadow-2xl rounded-l-3xl transform transition-transform duration-300 ease-out flex flex-col overflow-hidden ${isAddDrawerOpen ? 'translate-x-0' : 'translate-x-full'
              } ${isDarkMode ? 'bg-[#0F172A] border-l border-slate-800' : 'bg-white'}`}
          >
            {/* Drawer Header */}
            <div className={`flex items-center justify-between px-5 sm:px-6 py-4 border-b shrink-0 ${isDarkMode ? 'bg-[#0F172A]/90 border-slate-800 text-white' : 'bg-white/90 border-slate-200 text-slate-900'
              }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 via-purple-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/25 ring-2 ring-white/20">
                  <LuWarehouse size={20} />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-black tracking-tight">Add New Warehouse</h2>
                  <p className="text-[11px] text-slate-400">Register facility & pickup details</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsAddDrawerOpen(false)}
                aria-label="Close drawer"
                className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-white flex items-center justify-center transition-colors hover:scale-105 active:scale-95"
              >
                <IoClose size={18} />
              </button>
            </div>

            {/* Form Component inside drawer */}
            {isAddDrawerOpen && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <AddWarehousePopup
                  onClose={() => setIsAddDrawerOpen(false)}
                  onSuccess={handleAddSuccess}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AllWarehouses;